import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { FileText } from 'lucide-react';

const Imprint = ({ onBack }) => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-8 sm:py-12">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Back Button */}
        <Button 
          variant="outline" 
          onClick={onBack}
          className="mb-4 sm:mb-6 border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-sm sm:text-base"
        >
          ← {t('legal.backToHome', 'Zurück zur Startseite')}
        </Button>

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-emerald-600 mx-auto mb-3 sm:mb-4" />
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {t('imprint.title', 'Impressum')}
          </h1>
        </div>

        {/* Content */}
        <Card className="shadow-lg">
          <CardContent className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
            {/* Company Info */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section1.title', 'Angaben gemäß Informationspflicht')}
              </h2>
              <div className="space-y-2 text-sm sm:text-base text-gray-700">
                <p className="font-semibold text-lg sm:text-xl">Brillanz & Glanz</p>
                <p>Einzelunternehmen (Registrierung ausstehend)</p>
                <p className="mt-4">Schweiz</p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section2.title', 'Kontakt')}
              </h2>
              <div className="space-y-2 text-sm sm:text-base text-gray-700">
                <p><span className="font-semibold">{t('imprint.phone', 'Telefon')}:</span> +41 76 766 94 30</p>
                <p><span className="font-semibold">E-Mail:</span> <span className="break-all">thaqi.kujtim@gmx.net</span></p>
                <p><span className="font-semibold">Website:</span> www.brillanz-glanz.ch</p>
              </div>
            </section>

            {/* Management */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section3.title', 'Vertretungsberechtigte Personen')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700">
                {t('imprint.section3.representative', 'Geschäftsführer/Inhaber')}: [Name nach Registrierung]
              </p>
            </section>

            {/* Register */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section4.title', 'Handelsregister')}
              </h2>
              <div className="space-y-2 text-sm sm:text-base text-gray-700">
                <p className="text-gray-600 italic">Handelsregister-Eintragung ausstehend</p>
              </div>
            </section>

            {/* VAT */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section5.title', 'Umsatzsteuer-ID')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700">
                <span className="text-gray-600 italic">MWST-Nummer wird nach Registrierung ergänzt</span>
              </p>
            </section>

            {/* Responsible for Content */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section6.title', 'Verantwortlich für den Inhalt')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700">
                {t('imprint.section6.content', 'Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV')}: Brillanz & Glanz, Schweiz
              </p>
            </section>

            {/* Dispute Resolution */}
            <section className="bg-gray-50 p-4 sm:p-6 rounded-lg">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section7.title', 'Online-Streitbeilegung')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('imprint.section7.content', 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:')} 
                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline ml-1 break-all">
                  https://ec.europa.eu/consumers/odr
                </a>
              </p>
              <p className="text-sm sm:text-base text-gray-700 mt-4">
                {t('imprint.section7.email', 'Unsere E-Mail-Adresse finden Sie oben im Impressum.')}
              </p>
            </section>

            {/* Disclaimer */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('imprint.section8.title', 'Haftungsausschluss')}
              </h2>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-2">
                {t('imprint.section8.subtitle1', 'Haftung für Inhalte')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('imprint.section8.content1', 'Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.')}
              </p>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-2">
                {t('imprint.section8.subtitle2', 'Haftung für Links')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('imprint.section8.content2', 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter verantwortlich.')}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Imprint;